/** Characters Excel forbids anywhere in a worksheet name. */
const FORBIDDEN_SHEET_NAME_CHARS = /[*?:/\\[\]]/g;

/** Excel's hard limit on worksheet name length. */
const MAX_SHEET_NAME_LENGTH = 31;

/**
 * Turns an arbitrary label (often a user-defined template name or product reference) into a
 * worksheet name Excel — and ExcelJS's `addWorksheet` — will accept.
 *
 * ExcelJS throws on `* ? : \ / [ ]`, on a leading/trailing apostrophe, on an empty name and on
 * the reserved name `History`, and silently truncates past 31 characters. Forbidden characters
 * are replaced with a space, whitespace is collapsed, edge apostrophes are stripped, and the
 * result is cut to 31 characters.
 *
 * @param {string} name - The desired worksheet name.
 * @param {string} fallback - Used when nothing usable is left after cleaning.
 * @returns {string} A worksheet name ExcelJS will not reject.
 */
export function toSafeSheetName(name: string, fallback = 'Sheet1'): string {
  const cleaned = trimSheetNameEdges(String(name ?? '').replace(FORBIDDEN_SHEET_NAME_CHARS, ' ').replace(/\s+/g, ' '));
  const truncated = trimSheetNameEdges(cleaned.slice(0, MAX_SHEET_NAME_LENGTH));

  if (!truncated || truncated === 'History') {
    return fallback;
  }

  return truncated;
}

/**
 * Strips surrounding whitespace and apostrophes, which Excel rejects at either end of a name.
 *
 * @param {string} value - Candidate worksheet name.
 * @returns {string} The value without leading/trailing whitespace or apostrophes.
 */
function trimSheetNameEdges(value: string): string {
  return value.replace(/^[\s']+|[\s']+$/g, '');
}
